/* eslint-disable i18next/no-literal-string */
import React, { useEffect, useRef, useState } from 'react';
import { YouTubeNamespace, YouTubePlayerInstance } from './youtubeIframeApi';

type YouTubeWindow = Window & typeof globalThis & {
  YT?: YouTubeNamespace;
  onYouTubeIframeAPIReady?: () => void;
};

export interface YouTubePlayerProps {
  videoId: string;
  ariaLabel?: string;
  className?: string;
  testId?: string;
  onReady?: (duration: number) => void;
  onPlay?: () => void;
  onProgress?: (currentTime: number, duration: number) => void;
  onEnded?: (currentTime: number, duration: number) => void;
  onError?: (message: string, code?: number) => void;
}

const API_SCRIPT_ID = 'youtube-iframe-api';
const API_URL = 'https://www.youtube.com/iframe_api';
const API_LOAD_TIMEOUT_MS = 15_000;
const DURATION_LOAD_TIMEOUT_MS = 10_000;
const READY_STABILITY_MS = 750;
const PROGRESS_INTERVAL_MS = 250;
const MIN_SEEK_TOLERANCE_SECONDS = 1.25;
let apiPromise: Promise<YouTubeNamespace> | null = null;

const finiteOrZero = (value: number): number => Number.isFinite(value) && value >= 0 ? value : 0;

export const getYouTubePlayerErrorMessage = (code?: number): string => {
  if (code === 100) {
    return 'El video de YouTube no está disponible: puede ser privado, haberse eliminado o tener una URL incorrecta.';
  }
  if (code === 101 || code === 150) {
    return 'El propietario del video no permite reproducirlo dentro de esta aplicación. Elige otro video de YouTube que permita la inserción.';
  }
  if (code === 153) {
    return 'YouTube rechazó la identificación del reproductor. Abre la administración desde la URL pública HTTPS del estudio y vuelve a validar el enlace.';
  }
  if (code === 2) {
    return 'YouTube rechazó el enlace porque el identificador del video no es válido.';
  }
  if (code === 5) {
    return 'YouTube no pudo reproducir este video en el navegador. Prueba otro video compatible.';
  }
  return 'No se pudo cargar el video de YouTube. Comprueba la conexión y que el video siga disponible.';
};

const loadYouTubeApi = (): Promise<YouTubeNamespace> => {
  const youtubeWindow = window as YouTubeWindow;
  if (youtubeWindow.YT?.Player) return Promise.resolve(youtubeWindow.YT);
  if (apiPromise) return apiPromise;

  apiPromise = new Promise<YouTubeNamespace>((resolve, reject) => {
    let settled = false;
    const finish = (youtube: YouTubeNamespace) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      resolve(youtube);
    };
    const fail = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      apiPromise = null;
      reject(new Error(getYouTubePlayerErrorMessage()));
    };
    const timeout = window.setTimeout(fail, API_LOAD_TIMEOUT_MS);
    const previousReady = youtubeWindow.onYouTubeIframeAPIReady;

    youtubeWindow.onYouTubeIframeAPIReady = () => {
      try {
        previousReady?.();
      } finally {
        if (youtubeWindow.YT?.Player) finish(youtubeWindow.YT);
        else fail();
      }
    };

    const existingScript = document.getElementById(API_SCRIPT_ID) as HTMLScriptElement | null;
    if (existingScript) {
      existingScript.addEventListener('error', fail, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = API_SCRIPT_ID;
    script.src = API_URL;
    script.async = true;
    script.addEventListener('error', fail, { once: true });
    document.head.appendChild(script);
  });

  return apiPromise;
};

/**
 * YouTube iframe player for a watch-once flow. It fixes playback at 1x and
 * rolls forward seeks back to the furthest legitimately observed position.
 */
export const YouTubePlayer: React.FC<YouTubePlayerProps> = ({
  videoId,
  ariaLabel = 'Video de YouTube',
  className = '',
  testId,
  onReady,
  onPlay,
  onProgress,
  onEnded,
  onError,
}) => {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const callbacksRef = useRef({ onReady, onPlay, onProgress, onEnded, onError });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  callbacksRef.current = { onReady, onPlay, onProgress, onEnded, onError };

  useEffect(() => {
    let disposed = false;
    let player: YouTubePlayerInstance | null = null;
    let progressTimer: number | null = null;
    let durationTimer: number | null = null;
    let readyStabilityTimer: number | null = null;
    let readinessReported = false;
    let readinessScheduled = false;
    let furthestPosition = 0;
    let lastSampleTime = performance.now();

    setErrorMessage(null);

    const reportError = (message: string, code?: number) => {
      if (disposed) return;
      if (durationTimer !== null) window.clearTimeout(durationTimer);
      if (readyStabilityTimer !== null) window.clearTimeout(readyStabilityTimer);
      setErrorMessage(message);
      callbacksRef.current.onError?.(message, code);
    };

    const reportReadyDuration = (target: YouTubePlayerInstance) => {
      const duration = finiteOrZero(target.getDuration());
      if (!readinessReported && !readinessScheduled && duration > 0) {
        readinessScheduled = true;
        // Embedding refusals can arrive immediately after onReady.  Do not
        // enable the save action during that short race window.
        readyStabilityTimer = window.setTimeout(() => {
          if (disposed || !player || readinessReported) return;
          const stableDuration = finiteOrZero(player.getDuration());
          if (stableDuration <= 0) {
            readinessScheduled = false;
            return;
          }
          readinessReported = true;
          if (durationTimer !== null) window.clearTimeout(durationTimer);
          callbacksRef.current.onReady?.(stableDuration);
        }, READY_STABILITY_MS);
      }
      return duration;
    };

    const enforcePlaybackRate = (target: YouTubePlayerInstance) => {
      try {
        if (target.getPlaybackRate() !== 1) target.setPlaybackRate(1);
      } catch {
        // The player can briefly reject commands while changing state.
      }
    };

    const sampleProgress = () => {
      if (!player || disposed) return;
      const now = performance.now();
      const elapsedSeconds = Math.max(0, (now - lastSampleTime) / 1000);
      lastSampleTime = now;

      const currentTime = finiteOrZero(player.getCurrentTime());
      const duration = reportReadyDuration(player);
      const playing = player.getPlayerState() === 1;
      const allowedAdvance = playing
        ? Math.max(MIN_SEEK_TOLERANCE_SECONDS, elapsedSeconds * 1.5 + 0.5)
        : MIN_SEEK_TOLERANCE_SECONDS;

      if (currentTime > furthestPosition + allowedAdvance) {
        player.seekTo(furthestPosition, false);
        callbacksRef.current.onProgress?.(furthestPosition, duration);
        return;
      }

      if (playing) furthestPosition = Math.max(furthestPosition, currentTime);
      callbacksRef.current.onProgress?.(currentTime, duration);
      enforcePlaybackRate(player);
    };

    void loadYouTubeApi()
      .then((youtube) => {
        if (disposed || !mountRef.current) return;

        player = new youtube.Player(mountRef.current, {
          videoId,
          host: 'https://www.youtube-nocookie.com',
          width: '100%',
          height: '100%',
          playerVars: {
            controls: 1,
            disablekb: 1,
            playsinline: 1,
            rel: 0,
            origin: window.location.origin,
          },
          events: {
            onReady: ({ target }) => {
              if (disposed) return;
              player = target;
              enforcePlaybackRate(target);
              target.getIframe().setAttribute('title', ariaLabel);
              const duration = reportReadyDuration(target);
              callbacksRef.current.onProgress?.(0, duration);
              lastSampleTime = performance.now();
              progressTimer = window.setInterval(sampleProgress, PROGRESS_INTERVAL_MS);
              if (!readinessReported) {
                durationTimer = window.setTimeout(() => reportError(
                  'YouTube cargó el reproductor, pero no informó una duración. Comprueba que sea un video publicado y no una emisión en directo.',
                ), DURATION_LOAD_TIMEOUT_MS);
              }
            },
            onStateChange: ({ data, target }) => {
              if (disposed) return;
              enforcePlaybackRate(target);
              if (data === youtube.PlayerState.PLAYING) {
                lastSampleTime = performance.now();
                callbacksRef.current.onPlay?.();
              }
              if (data === youtube.PlayerState.ENDED) {
                const duration = finiteOrZero(target.getDuration());
                if (duration > 0 && duration > furthestPosition + MIN_SEEK_TOLERANCE_SECONDS) {
                  target.seekTo(furthestPosition, false);
                  target.pauseVideo();
                  callbacksRef.current.onProgress?.(furthestPosition, duration);
                  return;
                }
                furthestPosition = Math.max(furthestPosition, duration);
                callbacksRef.current.onProgress?.(duration, duration);
                callbacksRef.current.onEnded?.(duration, duration);
              }
            },
            onPlaybackRateChange: ({ target }) => enforcePlaybackRate(target),
            onError: ({ data }) => reportError(getYouTubePlayerErrorMessage(data), data),
          },
        });
      })
      .catch(() => reportError(getYouTubePlayerErrorMessage()));

    return () => {
      disposed = true;
      if (progressTimer !== null) window.clearInterval(progressTimer);
      if (durationTimer !== null) window.clearTimeout(durationTimer);
      if (readyStabilityTimer !== null) window.clearTimeout(readyStabilityTimer);
      try {
        player?.destroy();
      } catch {
        // The API may remove the iframe itself while the component unmounts.
      }
    };
  }, [ariaLabel, videoId]);

  return (
    <div
      className={className}
      data-testid={testId}
      style={{ aspectRatio: '16 / 9', width: '100%' }}
    >
      <div ref={mountRef} style={{ height: '100%', width: '100%' }} />
      {errorMessage && !onError && <p role="alert">{errorMessage}</p>}
    </div>
  );
};
